import { useState } from 'react';
import { Box, TextField, Typography, Paper, Tabs, Tab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ButtonBasic } from '../../../components';
import MitrolDataTable from './MitrolDataTable';
import DashboardAcuAgentes from './DashboardAcuAgentes';
import MitrolReportesService from '../../../services/mitrolReportes.service';
import { extractMitrolData } from '../helpers/mitrolHelpers';

const getDefaultDates = () => {
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 3 * 86400000).toISOString().split('T')[0];
  return { desde: weekAgo, hasta: today };
};

const PanelAcuAgentes = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const defaults = getDefaultDates();
  const [fechaDesde, setFechaDesde] = useState(defaults.desde);
  const [fechaHasta, setFechaHasta] = useState(defaults.hasta);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [viewTab, setViewTab] = useState(0); // 0 = Dashboard, 1 = Tabla

  const handleSubmit = async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    setResult(null);
    const start = performance.now();
    try {
      const resp = await MitrolReportesService.getAcuAgentes(fechaDesde, fechaHasta);
      setResult(resp);
    } catch (err) {
      setResult({ error: err?.response?.data?.error || err.message });
    } finally {
      setElapsed(Math.round(performance.now() - start));
      setLoading(false);
    }
  };

  // Extraer filas de Table1 para el dashboard
  const table1Rows = (() => {
    if (!result) return null;
    // Backend ya limpia los datos: result = { Table1: [...], Table2: [...] }
    if (Array.isArray(result.Table1)) return result.Table1;
    // Si viene envuelto en .data (respuesta sin interceptor)
    if (Array.isArray(result?.data?.Table1)) return result.data.Table1;
    // Legacy: estructura XML sin limpiar (diffgram)
    const extracted = extractMitrolData(result);
    if (extracted?.rows) return extracted.rows;
    if (Array.isArray(extracted)) return extracted;
    return null;
  })();

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          bgcolor: isDarkMode ? '#172554' : '#eff6ff',
          borderColor: isDarkMode ? '#1e40af' : '#93c5fd',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Reporte <strong>Acumuladores de Agentes</strong> — Seleccioná el rango de fechas y ejecutá
          el reporte. Los resultados se muestran como dashboard o tabla de datos.
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <TextField
          label="Fecha Desde"
          type="date"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required
          size="small"
        />
        <TextField
          label="Fecha Hasta"
          type="date"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
          required
          size="small"
        />
        <ButtonBasic
          textButton="Ejecutar Reporte"
          onClick={handleSubmit}
          isLoading={loading}
          variant="contained"
          sx={{ textTransform: 'none' }}
        />
        {elapsed != null && (
          <Typography variant="caption" color="text.secondary">
            {elapsed}ms
          </Typography>
        )}
      </Box>

      {/* Vista: Dashboard vs Tabla */}
      {result && !result.error && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={viewTab}
            onChange={(_, v) => setViewTab(v)}
            sx={{ minHeight: 36, mb: 1 }}
          >
            <Tab
              icon={<BarChartIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Dashboard"
              sx={{ minHeight: 36, textTransform: 'none', fontSize: 13 }}
            />
            <Tab
              icon={<TableChartIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Datos"
              sx={{ minHeight: 36, textTransform: 'none', fontSize: 13 }}
            />
          </Tabs>

          <Box sx={{ display: viewTab === 0 ? 'block' : 'none' }}>
            {table1Rows && <DashboardAcuAgentes rawRows={table1Rows} />}
          </Box>

          <Box sx={{ display: viewTab === 1 ? 'block' : 'none' }}>
            <MitrolDataTable data={result} panelId="acu" />
          </Box>
        </Box>
      )}

      {/* Mostrar error si existe */}
      {result?.error && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 2,
            bgcolor: isDarkMode ? '#3b0a0a' : '#fef2f2',
            borderColor: '#ef4444',
          }}
        >
          <Typography variant="body2" color="error">
            {typeof result.error === 'string' ? result.error : JSON.stringify(result.error)}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PanelAcuAgentes;
